"use client";

import {
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
  NumberField as NumberFieldRoot,
} from "@/components/ui/number-field";
import { Field, FieldDescription, FieldError, FieldLabel, useFieldWrapperContext } from "./field-wrapper";
import type { FieldBindingProps } from "./types";

export interface NumberFieldProps extends FieldBindingProps<number | null> {
  /** Omit only where the surrounding row already says what the number is; pass `ariaLabel` instead. */
  label?: string;
  ariaLabel?: string;
  description?: string;
  min?: number;
  max?: number;
  /** Preserve out-of-range text entry for validation instead of clamping on blur. */
  allowOutOfRange?: boolean;
  step?: number | "any";
  format?: Intl.NumberFormatOptions;
  disabled?: boolean;
  isRequired?: boolean;
  hideRequiredIndicator?: boolean;
}

const DECIMAL_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 20, useGrouping: false };

export function NumberField({
  label,
  description,
  errors,
  isTouched,
  isRequired,
  hideRequiredIndicator,
  ...props
}: NumberFieldProps) {
  return (
    <Field errors={errors} isTouched={isTouched} isRequired={isRequired && !hideRequiredIndicator}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldNumberInput {...props} label={label} description={description} isRequired={isRequired} />
      <FieldError />
    </Field>
  );
}

/**
 * Snap to the step's decimal places. Stepping adds in binary floating point, so
 * 1.37 + 0.01 + 0.01 lands on 1.3900000000000001, and that is what got saved.
 */
export function roundToStep(value: number | null, step: number | "any" | undefined): number | null {
  if (value === null || typeof step !== "number") return value;
  const decimals = String(step).split(".")[1]?.length ?? 0;
  return Number(value.toFixed(decimals));
}

function FieldNumberInput({
  value,
  onChange,
  onBlur,
  label,
  description,
  isRequired,
  ariaLabel,
  format = DECIMAL_FORMAT,
  ...props
}: NumberFieldProps) {
  const name = (label ?? ariaLabel ?? "value").toLowerCase();
  const { id, hasError } = useFieldWrapperContext();
  const describedBy = [description ? `${id}-desc` : null, hasError ? `${id}-error` : null].filter(Boolean).join(" ");
  return (
    <NumberFieldRoot
      {...props}
      id={id}
      value={value}
      onValueChange={(next) => onChange(roundToStep(next, props.step))}
      required={isRequired}
      format={format}
      size="lg"
      className={label ? "mt-1" : undefined}
    >
      {/* lg and shadow-xs match InputField and SelectField, so a number sits level with them in a row. */}
      <NumberFieldGroup aria-invalid={hasError} className="shadow-xs">
        <NumberFieldDecrement aria-label={`Decrease ${name}`} />
        <NumberFieldInput
          onBlur={onBlur}
          aria-label={label ? undefined : ariaLabel}
          aria-invalid={hasError}
          aria-describedby={describedBy || undefined}
        />
        <NumberFieldIncrement aria-label={`Increase ${name}`} />
      </NumberFieldGroup>
    </NumberFieldRoot>
  );
}
